import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchSchoolById, type SchoolResult } from "@/services/schools";

/** Maximum number of team ids honored from the `ids` query param. */
export const MAX_SCHOOL_IDS = 10;

/**
 * Parse a comma-separated `ids` query param into a clean list of team ids:
 * trimmed, empties dropped, deduped (first occurrence wins), capped at
 * MAX_SCHOOL_IDS. Returns [] for missing/empty input.
 */
export function parseSchoolIdsParam(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const part of raw.split(",")) {
    const id = part.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length >= MAX_SCHOOL_IDS) break;
  }
  return ids;
}

export interface SchoolsFromIdsParam {
  /** True while the page should show the ids-derived list instead of search results. */
  idsMode: boolean;
  /** Resolved schools, in the order the ids appeared in the URL. Failed ids are dropped. */
  schools: SchoolResult[];
  loading: boolean;
  /** Leave ids mode (e.g. when the user starts a normal search). */
  exitIdsMode: () => void;
}

/**
 * Supports deep links of the form `/schools?ids=<teamId1>,<teamId2>,...`
 * (e.g. from the JGS activity digest email). Fetches each id in parallel,
 * keeps the ones that resolve, and clears the URL params afterward —
 * extra tracking params (`src`, `d`, `cta`, ...) are dropped with them.
 * A single `?id=` param takes precedence and deactivates this hook.
 */
export function useSchoolsFromIdsParam(): SchoolsFromIdsParam {
  const [searchParams, setSearchParams] = useSearchParams();
  const idsParam = searchParams.get("ids");
  const singleIdParam = searchParams.get("id");

  const [idsMode, setIdsMode] = useState(
    () => !singleIdParam && parseSchoolIdsParam(idsParam).length > 0
  );
  const [schools, setSchools] = useState<SchoolResult[]>([]);
  const [loading, setLoading] = useState(idsMode);

  useEffect(() => {
    if (singleIdParam) return; // single-school deep link takes precedence
    const ids = parseSchoolIdsParam(idsParam);
    if (ids.length === 0) return;

    let cancelled = false;
    setIdsMode(true);
    setLoading(true);
    Promise.allSettled(ids.map(id => fetchSchoolById(id))).then(settled => {
      if (cancelled) return;
      const resolved: SchoolResult[] = [];
      for (const result of settled) {
        if (result.status === "fulfilled") resolved.push(result.value);
      }
      setSchools(resolved);
      setLoading(false);
      // Clean up URL (also drops tracking params like src/d/cta)
      setSearchParams({}, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [idsParam, singleIdParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const exitIdsMode = useCallback(() => setIdsMode(false), []);

  return { idsMode, schools, loading, exitIdsMode };
}
